import { useCallback, useEffect, useMemo, useState } from 'react';

import { overtimeApprovalService } from '@/api/domains/overtime/overtimeApproval.service';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import type {
  OvertimeApprovalMonthlyCut,
  OvertimeCounterKey,
  OvertimeCutsSource,
} from '@/api/domains/overtime/overtimeApproval.types';


export interface OvertimeApprovalCutFiltersPatch {
  filterCompanyId?: number | null;
  monthlyCutId?: number | null;
  counterFilter?: OvertimeCounterKey;
}

export interface UseOvertimeApprovalMonthlyCutsParams {
  selectedCompanyValue: string;
  /** El padre fusiona esto con el snapshot de filtros avanzados pendiente y llama setFilters. */
  onFiltersPatch: (patch: OvertimeApprovalCutFiltersPatch) => void;
}

export interface UseOvertimeApprovalMonthlyCutsResult {
  isLoadingCuts: boolean;
  hasCuts: boolean;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  descriptionOptions: { value: string; label: string }[];
  selectedYear: number | null;
  selectedMonth: number | null;
  onYearChange: (value: string) => void;
  onMonthChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  noCutsMessage: string | null;
}

const ALL_COMPANIES_VALUE = 'all';

/**
 * Empresa + período (cortes mensuales) de Aprobación HHEE. Extraído de useOvertimeApproval para no
 * dejar ese hook monolítico. No conoce los filtros avanzados: en vez de escribir `filters`
 * directamente, delega en `onFiltersPatch` (que el padre fusiona con el snapshot avanzado
 * pendiente) — así evita depender de `dimensionalFilter`, que a su vez depende de datos que salen
 * de este mismo hook (lockedCompanyIds), rompiendo lo que sería una dependencia circular.
 */
export function useOvertimeApprovalMonthlyCuts(
  params: UseOvertimeApprovalMonthlyCutsParams,
): UseOvertimeApprovalMonthlyCutsResult {
  const { selectedCompanyValue, onFiltersPatch } = params;

  const { t: translate } = useTranslation();
  const { showApiError } = useUI();

  const [cuts, setCuts] = useState<OvertimeApprovalMonthlyCut[]>([]);
  const [cutsSource, setCutsSource] = useState<OvertimeCutsSource>('none');
  const [isLoadingCuts, setIsLoadingCuts] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  // Carga de cortes cada vez que cambia la empresa del filtro principal.
  useEffect(() => {
    if (!selectedCompanyValue) return;

    const controller = new AbortController();
    const isAllCompanies = selectedCompanyValue === ALL_COMPANIES_VALUE;
    const filterCompanyId = isAllCompanies ? null : Number(selectedCompanyValue);

    const loadCuts = async () => {
      setIsLoadingCuts(true);

      const response = await overtimeApprovalService.getMonthlyCuts(
        filterCompanyId,
        controller.signal,
      );

      if (controller.signal.aborted) return;

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('attendance:overtimeApproval.errors.loadMonthlyCuts'));
        }
        setCuts([]);
        setCutsSource('none');
        setIsLoadingCuts(false);
        return;
      }

      const { monthlyCuts, defaultMonthlyCutId, cutsSource: source } = response.data;

      setCuts(monthlyCuts);
      setCutsSource(source);
      setIsLoadingCuts(false);

      const defaultCut = monthlyCuts.find((cut) => cut.monthlyCutId === defaultMonthlyCutId)
        ?? monthlyCuts[0]
        ?? null;

      setSelectedYear(defaultCut?.year ?? null);
      setSelectedMonth(defaultCut?.month ?? null);
      // Se aplican también los avanzados vigentes: al cambiar de empresa el panel ya sincronizó su
      // filtro de Empresa, y dejarlo fuera provocaría una segunda consulta al vencer el debounce.
      onFiltersPatch({
        filterCompanyId,
        monthlyCutId: defaultCut?.monthlyCutId ?? null,
        counterFilter: 'calculated',
      });
    };

    void loadCuts();

    return () => controller.abort();
  }, [selectedCompanyValue, showApiError, translate, onFiltersPatch]);

  const hasCuts = cutsSource !== 'none' && cuts.length > 0;

  const onYearChange = useCallback((value: string) => {
    const year = Number(value);
    const candidates = cuts.filter((cut) => cut.year === year);
    const cut = candidates.filter((candidate) => candidate.month === selectedMonth)[0]
      ?? candidates[0]
      ?? cuts[0]
      ?? null;

    setSelectedYear(cut?.year ?? null);
    setSelectedMonth(cut?.month ?? null);
    onFiltersPatch({ monthlyCutId: cut?.monthlyCutId ?? null });
  }, [cuts, selectedMonth, onFiltersPatch]);

  const onMonthChange = useCallback((value: string) => {
    const month = Number(value);
    const candidates = cuts.filter((cut) => cut.year === selectedYear && cut.month === month);
    const cut = candidates[0] ?? cuts[0] ?? null;

    setSelectedYear(cut?.year ?? null);
    setSelectedMonth(cut?.month ?? null);
    onFiltersPatch({ monthlyCutId: cut?.monthlyCutId ?? null });
  }, [cuts, selectedYear, onFiltersPatch]);

  const onDescriptionChange = useCallback((value: string) => {
    const monthlyCutId = Number(value);
    const cut = cuts.find((candidate) => candidate.monthlyCutId === monthlyCutId) ?? null;

    setSelectedYear(cut?.year ?? null);
    setSelectedMonth(cut?.month ?? null);
    onFiltersPatch({ monthlyCutId: cut?.monthlyCutId ?? null });
  }, [cuts, onFiltersPatch]);

  const yearOptions = useMemo(() => {
    const years = [...new Set(cuts.map((cut) => cut.year))].sort((a, b) => b - a);
    return years.map((year) => ({ value: String(year), label: String(year) }));
  }, [cuts]);

  const monthOptions = useMemo(() => {
    const monthsOfYear = cuts.filter((cut) => cut.year === selectedYear);
    const seen = new Map<number, string>();
    for (const cut of monthsOfYear) {
      if (!seen.has(cut.month)) seen.set(cut.month, cut.monthName);
    }
    return [...seen.entries()]
      .sort(([firstMonth], [secondMonth]) => secondMonth - firstMonth)
      .map(([month, monthName]) => ({ value: String(month), label: monthName }));
  }, [cuts, selectedYear]);

  const descriptionOptions = useMemo(() => cuts
    .filter((cut) => cut.year === selectedYear && cut.month === selectedMonth)
    .map((cut) => ({ value: String(cut.monthlyCutId), label: cut.description })),
  [cuts, selectedYear, selectedMonth]);

  const noCutsMessage = useMemo(() => {
    if (isLoadingCuts || hasCuts || !selectedCompanyValue) return null;

    return selectedCompanyValue === ALL_COMPANIES_VALUE
      ? translate('attendance:overtimeApproval.messages.noHoldingCuts')
      : translate('attendance:overtimeApproval.messages.noCompanyCuts');
  }, [isLoadingCuts, hasCuts, selectedCompanyValue, translate]);

  return {
    isLoadingCuts,
    hasCuts,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    noCutsMessage,
  };
}

export default useOvertimeApprovalMonthlyCuts;
